import React, { type FC, type ReactNode, useState, useEffect } from 'react'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/form'
import { Input } from '../../components/input'
import { Button } from '../../components/button'

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import RichTextEditor from '../RichTextEditor'
import MediaUploader from '../MediaUploader'
import { Check, LoaderCircle, ChevronDown } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@univdiam/ui/components/select'
import { Popover, PopoverContent, PopoverTrigger } from '@univdiam/ui/components/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@univdiam/ui/components/command'
import { cn } from '@univdiam/ui/lib/utils'
import ErrorComponent from '../ErrorComponent'
import axios from 'axios'
import type { Customer } from '@univdiam/ui/type'

type SizeType = {
    'Ring': number[];
    'Earring': string[];
    'Bracelet': number[];
    'Pendant': number[];
    'Necklace': number[];
};

const orderFormSchema = z.object({
    customerID: z.string().optional(),
    jobTitle: z.string().min(1, {
        message: "This field is required",
    }).max(150, "Job Title cannot exceed 150 characters."),
    productType: z.string(),
    productName: z.string().max(255, "Product Name cannot exceed 255 characters.").optional(),
    quantity: z.string().max(10, "Quantity is too large."),
    size: z.string().max(50, "Size cannot exceed 50 characters."),
    metalType: z.array(z.string().max(60)).max(10),
    designType: z.string(),
    diamondQuality: z.array(z.string().max(60)).max(10),
    specialInstruction: z.string().max(5000, "Special Instructions cannot exceed 5000 characters."),
    resinSample: z.boolean(),
    attachments: z.array(z.string())
})

type OrderFormType = {
    children?: ReactNode,
    onSubmit: (values: z.infer<typeof orderFormSchema>) => void,
    handleSendToUpload: (files: File[]) => Promise<string[]>,
    mediaLoading: boolean,
    isError?: boolean,
    isLoading?: boolean,
    error?: Error | null,
    isAdmin?: boolean
    customerData?: Customer[]
    setCustomerSearchQuery?: (search: string) => void
    fetchNextPageCustomer?: () => void
    hasNextPageCustomer?: boolean
    isFetchingNextPageCustomer?: boolean
}

type MultiSelectWithOtherProps = {
    value: string[]
    onChange: (value: string[]) => void
    options: string[]
    placeholder: string
    otherPlaceholder: string
}

// Checkbox list + free-text entry at the bottom. Custom entries show up as checked rows; unchecking removes them.
const MultiSelectWithOther: FC<MultiSelectWithOtherProps> = ({ value, onChange, options, placeholder, otherPlaceholder }) => {
    const [other, setOther] = useState("")
    const rows = [...options, ...value.filter((v) => !options.includes(v))]
    const toggle = (o: string) => onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o])
    const addOther = () => {
        const v = other.trim()
        if (v && !value.includes(v) && value.length < 10) onChange([...value, v])
        setOther("")
    }

    return (
        <Popover>
            <PopoverTrigger asChild>
                <FormControl className="mt-[6px]">
                    <button
                        type="button"
                        className={cn("border-input flex w-full h-9 items-center justify-between gap-2 rounded-md border bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]", !value.length && "text-muted-foreground")}
                    >
                        <span className="truncate text-left">{value.length ? value.join(", ") : placeholder}</span>
                        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                    </button>
                </FormControl>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-(--radix-popover-trigger-width) p-1">
                {rows.map((o) => {
                    const checked = value.includes(o)
                    return (
                        <button
                            key={o}
                            type="button"
                            role="checkbox"
                            aria-checked={checked}
                            onClick={() => toggle(o)}
                            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent focus-visible:bg-accent outline-none"
                        >
                            <span className={cn("flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border transition-colors", checked ? "border-black bg-black text-white" : "border-[#777777]")}>
                                {checked && <Check className="h-3 w-3" />}
                            </span>
                            <span className="truncate">{o}</span>
                        </button>
                    )
                })}
                <div className="mt-1 border-t pt-1 flex items-center gap-1">
                    <Input
                        value={other}
                        onChange={(e) => setOther(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault()
                                addOther()
                            }
                        }}
                        placeholder={otherPlaceholder}
                        maxLength={60}
                        className="h-8 border-0 shadow-none focus-visible:ring-0"
                    />
                    {other.trim() && (
                        <button type="button" onClick={addOther} className="shrink-0 rounded-sm px-2 h-8 text-sm font-medium hover:bg-accent">
                            Add
                        </button>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    )
}

const OrderForm: FC<OrderFormType> = ({ onSubmit, handleSendToUpload, mediaLoading, isError, isLoading, error, isAdmin, customerData, setCustomerSearchQuery, fetchNextPageCustomer, hasNextPageCustomer, isFetchingNextPageCustomer }) => {
    const [openCombobox, setOpenCombobox] = useState(false)
    const [searchVal, setSearchVal] = useState("")

    useEffect(() => {
        const timer = setTimeout(() => {
            if (setCustomerSearchQuery) {
                setCustomerSearchQuery(searchVal)
            }
        }, 500)
        return () => clearTimeout(timer)
    }, [searchVal, setCustomerSearchQuery])

    const form = useForm<z.infer<typeof orderFormSchema>>({
        resolver: zodResolver(orderFormSchema),
        defaultValues: {
            customerID: "",
            jobTitle: "",
            productType: "",
            quantity: '1',
            size: "",
            metalType: [],
            designType: "",
            diamondQuality: [],
            specialInstruction: "",
            resinSample: false,
            attachments: []
        },
    })

    const PRODUCTS = [
        'Ring',
        'Earring',
        'Bracelet',
        'Pendant',
        'Necklace',
        'Other'
    ]

    const METAL_TYPE = [
        '10k White',
        '10k Yellow',
        '10k Rose',
        '14k White',
        '14k Yellow',
        '14k Rose',
        '18k White',
        '18k Yellow',
        '18k Rose',
        'Platinum'
    ]

    const DESIGN_TYPE = [
        { value: 'Pre Cad Quote', description: 'A quick, preliminary estimate provided within 24 hours.' },
        { value: 'Cad Quote', description: 'An estimate based on 3D modeling, delivered in 2-3 business days.' }
    ]

    const DIAMOND_QUALITY = [
        'Lab Grown F-VS',
        'Natural GH-SI',
        'Natural F-VS'
    ]

    const productTypeIdentity = form.watch('productType')

    const SIZES: SizeType = {
        'Ring': [3, 3.25, 3.5, 3.75,
            4, 4.25, 4.5, 4.75,
            5, 5.25, 5.5, 5.75,
            6, 6.25, 6.5, 6.75,
            7, 7.25, 7.5, 7.75,
            8, 8.25, 8.5, 8.75,
            9, 9.25, 9.5, 9.75,
            10, 10.25, 10.5, 10.75,
            11, 11.25, 11.5, 11.75,
            12, 12.25, 12.5, 12.75,
            13, 13.25, 13.5, 13.75,
            14, 14.25, 14.5, 14.75,
            15],
        'Earring': [
            'Stud',
            'Halo',
            'Hoops'
        ],
        'Bracelet': [ // inch
            6.5, 6.75,
            7, 7.25, 7.5, 7.75,
            8, 8.25, 8.5, 8.75,
            9
        ],
        'Pendant': [14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 29, 30], // inch
        'Necklace': [14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 29, 30] // inch

    }

    const isAxiosError = axios.isAxiosError(error)
    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                {
                    isAdmin && <FormField
                        control={form.control}
                        name="customerID"
                        render={({ field }) => (
                            <FormItem className="mb-0 w-full mb-[16px] flex flex-col">
                                <FormLabel className="text-[#38404D]">
                                    Select Customer <span className='text-[#16111]'>*</span>
                                </FormLabel>
                                <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
                                    <PopoverTrigger asChild>
                                        <FormControl>
                                            <button
                                                type="button"
                                                role="combobox"
                                                aria-expanded={openCombobox}
                                                className={cn("flex w-full items-center justify-between rounded-md border border-input bg-transparent px-3 h-[36px] text-sm font-normal outline-none disabled:cursor-not-allowed disabled:opacity-50", !field.value && "text-muted-foreground")}
                                            >
                                                <span className="truncate text-left">
                                                    {field.value
                                                        ? customerData?.find((customer) => String(customer.customer_id) === field.value)?.business_name || customerData?.find((customer) => String(customer.customer_id) === field.value)?.full_name
                                                        : "Select Customer"}
                                                </span>
                                                <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                                            </button>
                                        </FormControl>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-full p-0" align="start">
                                        <Command shouldFilter={false}>
                                            <CommandInput 
                                                placeholder="Search customer..." 
                                                value={searchVal} 
                                                onValueChange={setSearchVal} 
                                            />
                                            <CommandEmpty>No customer found.</CommandEmpty>
                                            <CommandGroup>
                                                <CommandList 
                                                    onScroll={(e) => {
                                                        const target = e.currentTarget;
                                                        if (target.scrollHeight - target.scrollTop <= target.clientHeight + 50) {
                                                            if (hasNextPageCustomer && fetchNextPageCustomer && !isFetchingNextPageCustomer) {
                                                                fetchNextPageCustomer();
                                                            }
                                                        }
                                                    }}
                                                    className="max-h-[300px] overflow-y-auto"
                                                >
                                                    {customerData?.map((customer) => (
                                                        <CommandItem
                                                            key={customer.customer_id}
                                                            value={String(customer.customer_id)}
                                                            onSelect={(currentValue) => {
                                                                form.setValue("customerID", currentValue)
                                                                setOpenCombobox(false)
                                                            }}
                                                        >
                                                            <Check
                                                                className={cn(
                                                                    "mr-2 h-4 w-4",
                                                                    field.value === String(customer.customer_id) ? "opacity-100" : "opacity-0"
                                                                )}
                                                            />
                                                            {customer.business_name || customer.full_name}
                                                        </CommandItem>
                                                    ))}
                                                    {isFetchingNextPageCustomer && (
                                                        <div className="flex items-center justify-center p-4">
                                                            <LoaderCircle className="h-4 w-4 animate-spin text-muted-foreground" />
                                                        </div>
                                                    )}
                                                </CommandList>
                                            </CommandGroup>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                }
                <FormField
                    control={form.control}
                    name="jobTitle"
                    render={({ field }) => (
                        <FormItem className='mb-0 w-full'>
                            <FormLabel className='text-[#38404D]'>Job Title (P. O.)<span className='text-[#16111]'>*</span></FormLabel>
                            <FormControl className='mt-[6px]'>
                                <Input placeholder="Emily’s Birthday 14kt white rose ring" {...field} maxLength={150} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <div className='flex flex-col sm:flex-row items-start sm:items-center justify-center gap-[16px] mb-0'>
                    <FormField
                        control={form.control}
                        name="productType"
                        render={({ field }) => (
                            <FormItem className="mb-0 w-full mt-[16px]">
                                <FormLabel className="text-[#38404D]">
                                    Product Type
                                </FormLabel>
                                <FormControl className="mt-[6px] w-full">
                                    <Select value={field.value} onValueChange={field.onChange}
                                        {...form.register('productType', { required: true })}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Select Product" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {PRODUCTS.map((p) => (
                                                <SelectItem key={p} value={p}>
                                                    {p}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="quantity"
                        render={({ field }) => (
                            <FormItem className='mb-0 w-full mt-[16px]'>
                                <FormLabel className='text-[#38404D]'>Quantity</FormLabel>
                                <FormControl className='mt-[6px]'>
                                    <Input placeholder="Enter quantity" {...field} maxLength={10} onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div className='flex flex-col sm:flex-row items-start sm:items-center justify-center gap-[16px] mb-0'>
                    {
                        productTypeIdentity === 'Other' ? <div className='flex flex-col sm:flex-row items-start sm:items-center justify-center gap-[16px] mb-0 w-full'>
                            <FormField
                                control={form.control}
                                name="productName"
                                render={({ field }) => (
                                    <FormItem className='mb-0 w-full mt-[16px]'>
                                        <FormLabel className='text-[#38404D]'>Define your product</FormLabel>
                                        <FormControl className='mt-[6px]'>
                                            <Input placeholder="Custom product" {...field} maxLength={255} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="size"
                                render={({ field }) => (
                                    <FormItem className='mb-0 w-full mt-[16px]'>
                                        <FormLabel className='text-[#38404D]'>Size</FormLabel>
                                        <FormControl className='mt-[6px]'>
                                            <Input placeholder="Size" {...field} maxLength={50} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div> : <FormField
                            control={form.control}
                            name="size"
                            render={({ field }) => (
                                <FormItem className='mb-0 w-full mt-[16px]'>
                                    <FormLabel className='text-[#38404D]'>{productTypeIdentity} Size {(productTypeIdentity === 'Bracelet' || productTypeIdentity === 'Necklace') && '(inches)'}</FormLabel>
                                    <FormControl className='mt-[6px]'>
                                        <Select value={field.value} onValueChange={field.onChange} disabled={productTypeIdentity === 'Earring' || productTypeIdentity === 'Pendant'}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue placeholder="Select Size" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {SIZES[productTypeIdentity as 'Ring' | 'Earring' | 'Bracelet' | 'Pendant' | 'Necklace']?.map((m) => (
                                                    <SelectItem key={m} value={String(m)}>
                                                        {m}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    }

                    <FormField
                        control={form.control}
                        name="metalType"
                        render={({ field }) => (
                            <FormItem className="mb-0 w-full mt-[16px]">
                                <FormLabel className="text-[#38404D]">
                                    Metal Type
                                </FormLabel>
                                <MultiSelectWithOther
                                    value={field.value}
                                    onChange={field.onChange}
                                    options={METAL_TYPE}
                                    placeholder="Select Metal Type"
                                    otherPlaceholder="Other metal, e.g. Two-tone"
                                />
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div className='flex flex-col sm:flex-row items-start sm:items-center justify-center gap-[16px] mb-0'>
                    <FormField
                        control={form.control}
                        name="designType"
                        render={({ field }) => (
                            <FormItem className="mb-0 w-full mt-[16px]">
                                <FormLabel className="text-[#38404D]">
                                    Order Type
                                </FormLabel>
                                <FormControl className="mt-[6px] w-full">
                                    <Select value={field.value} onValueChange={field.onChange}>
                                        <SelectTrigger className="w-full">
                                            {/* explicit children so the trigger shows only the name, not the item's description */}
                                            <SelectValue placeholder="Select Order Type">{field.value}</SelectValue>
                                        </SelectTrigger>
                                        <SelectContent className="max-w-(--radix-select-trigger-width)">
                                            {DESIGN_TYPE.map((o) => (
                                                <SelectItem key={o.value} value={o.value} className="py-2">
                                                    <div className="flex flex-col gap-[2px] whitespace-normal">
                                                        <span>{o.value}</span>
                                                        <span className="text-[#777777] text-xs">{o.description}</span>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="diamondQuality"
                        render={({ field }) => (
                            <FormItem className="mb-0 w-full mt-[16px]">
                                <FormLabel className="text-[#38404D]">
                                    Stone Type
                                </FormLabel>
                                <MultiSelectWithOther
                                    value={field.value}
                                    onChange={field.onChange}
                                    options={DIAMOND_QUALITY}
                                    placeholder="Select Stone Type"
                                    otherPlaceholder="Other stone, e.g. Sapphire"
                                />
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>

                <FormField
                    control={form.control}
                    name="specialInstruction"
                    render={({ field }) => (
                        <FormItem className='mb-0 w-full mt-[16px]'>
                            <FormLabel className='text-[#38404D]'>Special Instructions</FormLabel>
                            <FormControl className='mt-[6px]'>
                                <RichTextEditor value={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />


                <FormField
                    control={form.control}
                    name="attachments"
                    render={({ field }) => (
                        <FormItem className='mb-0 w-full mt-[16px]'>
                            <FormLabel className='text-[#38404D]'>Media Attachment</FormLabel>
                            <FormControl className='mt-[6px]'>
                                <MediaUploader handleSendToUpload={async (files) => {
                                    const urls = await handleSendToUpload(files)
                                    const currentAttachments = form.getValues("attachments") || []
                                    form.setValue("attachments", [...currentAttachments, ...urls])
                                }} onRemove={(index) => {
                                    const currentAttachments = form.getValues("attachments") || []
                                    form.setValue("attachments", currentAttachments.filter((_, i) => i !== index))
                                }} value={field.value} mediaLoading={mediaLoading} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="resinSample"
                    render={({ field }) => (
                        <FormItem className="mb-0 w-full mt-[16px]">
                            <FormLabel className="text-[#38404D]">Resin Sample</FormLabel>
                            <p className="text-[#777777] text-caption2 mb-2">
                                Request a resin sample prior to manufacturing?
                            </p>
                            <FormControl>
                                <div
                                    className="flex items-center gap-[8px] cursor-pointer select-none"
                                    onClick={() => field.onChange(!field.value)}
                                >
                                    <div
                                        className={`w-5 h-5 border mb-[2px] ${field.value ? "border-[#000000]" : "border-[#777777]"
                                            } rounded-sm flex items-center justify-center transition-colors`}
                                    >
                                        {field.value && <Check className="w-4 h-4" />}
                                    </div>
                                    <span className={field.value ? "font-semibold" : "font-normal"}>
                                        Resin Sample: $25
                                    </span>
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />



                <div className='mt-[16px]'>
                    {
                        isError && <ErrorComponent message={isAxiosError ? error?.response?.data?.error : ''} />
                    }
                </div>

                <div className='mt-[32px] flex items-center gap-[16px] mb-0'>
                    <Button type="submit" className='w-fit ' isLoading={isLoading}>
    {'Submit Custom Request'}
</Button>
                    {/* <Button type="submit" className='w-fit' variant={'white'}>Save as Draft</Button> */}
                </div>
                <div className='mt-3'>
                    <p className='text-[#777777] italic'>CAD renders and quotes will be provided within 24-48 hours, free of charge.  We will contact you if any additional information is required.</p>
                </div>

            </form>
        </Form>
    )

}

export { OrderForm, orderFormSchema }